import { useState, useRef, useCallback, useEffect } from "react";
import { router } from "@inertiajs/react";
import { useCampaignService } from "@api/campaigns.hooks";
import type { UpdateCampaignRequestBody } from "@rails_api_base";

interface UseDebouncedSaveOptions {
  campaignId: number | undefined;
  debounceMs?: number;
}

export function useDebouncedSave({
  campaignId,
  debounceMs = 750,
}: UseDebouncedSaveOptions) {
  const service = useCampaignService();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const controllerRef = useRef<AbortController>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const send = useCallback(
    async (data: Partial<UpdateCampaignRequestBody>) => {
      if (!campaignId) return;

      controllerRef.current?.abort();
      controllerRef.current = new AbortController();

      setIsSaving(true);
      setError(null);
      try {
        await service.update(
          campaignId,
          data as UpdateCampaignRequestBody,
          controllerRef.current.signal
        );
        router.reload({
          only: [
            "campaign",
            "ad_group",
            "ad",
            "headlines",
            "descriptions",
            "callouts",
            "keywords",
            "negative_keywords",
            "structured_snippet",
            "ad_schedule",
            "location_targets",
            "languages",
          ],
        });
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
      } finally {
        setIsSaving(false);
      }
    },
    [campaignId, service]
  );

  const save = useCallback(
    (data: Partial<UpdateCampaignRequestBody>) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => send(data), debounceMs);
    },
    [send, debounceMs]
  );

  const saveNow = useCallback(
    async (data: Partial<UpdateCampaignRequestBody>) => {
      clearTimeout(timerRef.current);
      await send(data);
    },
    [send]
  );

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      controllerRef.current?.abort();
    },
    []
  );

  const cancel = useCallback(() => {
    clearTimeout(timerRef.current);
    controllerRef.current?.abort();
  }, []);

  return { save, saveNow, cancel, isSaving, error };
}
